import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AddActionButton } from "../AddActionButton";
import { ReportAccordionItem } from "../ReportAccordion";
import { ValuePicker } from "../ValuePicker";
import {
    categorizationRuleIssues,
    columnMatchesKey,
    dataColumnSelectionKey,
    isCategorizationRuleReady,
    isRuleGroupNode,
    makeCategorizationRule,
    makeRuleSet,
    reportAllColumns,
    reportTimeComparableColumns,
    ruleLeafConditions,
    ruleLogicOutline,
    ruleResultLabel,
    ruleValueSourceColumns,
    simulateRuleSet,
    targetFields,
} from "../../reportConfigurationLogic";
import type { CategorizationRule, DataColumn, ReportConfig, ReportConfigurationLookupOption, RuleNode, RuleActionKey, RuleSet, RuleValueSource } from "../../types";
import { columnDataTypeCategory } from "../../logic/operatorCatalog";
import { selectedDetailRuleSetIds, usesDetailRuleSetList } from "../../logic/detailRuleSetSelection";
import { combinedRowLabel, combinedRows, combinedRuleSetIds, usesCombinedRuleSets } from "../../logic/combinedRuleSetSelection";
import { removeCombinedRuleSet } from "../../logic/combinedRuleSets";
import { DestructiveConfirmDialog } from "../DestructiveConfirmDialog";

// A rule set is "in use" by a metric through the single breakdown picker, or - for Full Row
// Detail metrics - through the multi-select list, or as one of a combined table's rule sets.
function sectionUsesRuleSet(section: ReportConfig["sections"][number], ruleSetId: string) {
    if (usesDetailRuleSetList(section)) return selectedDetailRuleSetIds(section).includes(ruleSetId);
    if (usesCombinedRuleSets(section)) return combinedRuleSetIds(section).includes(ruleSetId);
    return section.breakdownSource === "ruleSet" && section.ruleSetId === ruleSetId;
}

// "Metric: row" for every combined-table row that deleting this rule set would remove.
function combinedRowsUsingRuleSet(report: ReportConfig, ruleSetId: string) {
    return report.sections
        .filter((section) => combinedRuleSetIds(section).includes(ruleSetId))
        .flatMap((section) => {
            const rows = combinedRows(section).filter((row) => row.ruleSetId === ruleSetId);
            return rows.length
                ? rows.map((row) => `${section.name || "Untitled metric"}: ${combinedRowLabel(row)}`)
                : [`${section.name || "Untitled metric"} (no rows yet)`];
        });
}
import { InlineIcon, IconAction, StatusIcon } from "../shared/EditorControls";
import { RuleConditionEditor, RULE_LOGIC_OPTIONS, ruleLevelLogicPlaceholder, setRuleLevelUniformJoin } from "../shared/RuleConditionEditor";
import type { RuleTreeCtx } from "../shared/RuleConditionEditor";

type CategorizationEditorProps = {
    report: ReportConfig;
    selection: {
        openRuleSetId: string;
        setOpenRuleSetId: React.Dispatch<React.SetStateAction<string>>;
        openRuleIdsBySet: Record<string, string>;
        setOpenRuleIdsBySet: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    };
    editing: {
        reserveUniqueId: (prefix: string, usedIds: Set<string>) => string;
        replaceSelectedReport: (updater: (report: ReportConfig) => ReportConfig) => void;
        revealAccordionItem: (id: string) => void;
    };
    catalog: {
        lookupOptionsForReportColumn: (report: ReportConfig, columnKey: string) => ReportConfigurationLookupOption[];
        ruleActionOptions: { label: string; key: RuleActionKey; }[];
        lookupLabelForReportColumn: (report: ReportConfig, columnKey: string, value: string) => string;
    };
    session: {
        simInputs: Record<string, string>;
        setSimInputs: React.Dispatch<React.SetStateAction<Record<string, string>>>;
        setSimRan: React.Dispatch<React.SetStateAction<boolean>>;
        simRan: boolean;
    };
};

type RuleMoreTooltipStyle = React.CSSProperties & {
    "--rc-rule-tooltip-arrow-left"?: string;
    "--rc-rule-tooltip-max-height"?: string;
};

function RuleMoreTooltip({ summary, disabled, children }: { summary: React.ReactNode; disabled: boolean; children: React.ReactNode }) {
    const lineRef = useRef<HTMLSpanElement>(null);
    const summaryRef = useRef<HTMLSpanElement>(null);
    const anchorRef = useRef<HTMLSpanElement>(null);
    const closeTimerRef = useRef<number | null>(null);
    const [hasOverflow, setHasOverflow] = useState(false);
    const [popover, setPopover] = useState<null | { style: RuleMoreTooltipStyle; placement: "above" | "below" }>(null);

    const clearCloseTimer = () => {
        if (closeTimerRef.current === null) return;
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
    };

    const updatePosition = () => {
        const anchor = anchorRef.current;
        if (!anchor) return;

        const rect = anchor.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const width = Math.min(460, Math.max(300, viewportWidth - 32));
        const anchorCenter = rect.left + rect.width / 2;
        const left = Math.min(Math.max(16, anchorCenter - width + 34), Math.max(16, viewportWidth - width - 16));
        const spaceBelow = viewportHeight - rect.bottom - 16;
        const spaceAbove = rect.top - 16;
        const estimatedHeight = 320;
        const placement = spaceBelow >= Math.min(estimatedHeight, 300) || spaceBelow >= spaceAbove ? "below" : "above";
        const top = placement === "below"
            ? rect.bottom + 12
            : rect.top - 12;
        const arrowLeft = Math.min(width - 22, Math.max(22, anchorCenter - left));
        const maxHeight = Math.max(96, placement === "below"
            ? viewportHeight - top - 16
            : top - 16);

        setPopover({
            placement,
            style: {
                left,
                top,
                width,
                "--rc-rule-tooltip-arrow-left": `${arrowLeft}px`,
                "--rc-rule-tooltip-max-height": `${maxHeight}px`,
            },
        });
    };

    const open = () => {
        if (disabled) return;
        clearCloseTimer();
        updatePosition();
    };

    const close = () => {
        clearCloseTimer();
        closeTimerRef.current = window.setTimeout(() => setPopover(null), 120);
    };

    useEffect(() => {
        if (!popover) return;
        const handleViewportChange = () => updatePosition();
        window.addEventListener("scroll", handleViewportChange, true);
        window.addEventListener("resize", handleViewportChange);
        return () => {
            window.removeEventListener("scroll", handleViewportChange, true);
            window.removeEventListener("resize", handleViewportChange);
        };
    }, [popover]);

    useEffect(() => {
        const measure = () => {
            const summaryElement = summaryRef.current;
            if (!summaryElement) return;
            setHasOverflow(summaryElement.scrollWidth > summaryElement.clientWidth + 1);
        };

        measure();
        window.addEventListener("resize", measure);
        const observer = typeof ResizeObserver !== "undefined" && lineRef.current
            ? new ResizeObserver(measure)
            : null;
        if (observer && lineRef.current) observer.observe(lineRef.current);

        return () => {
            window.removeEventListener("resize", measure);
            observer?.disconnect();
        };
    }, [summary]);

    useEffect(() => {
        if (disabled || !hasOverflow) setPopover(null);
    }, [disabled, hasOverflow]);

    useEffect(() => () => clearCloseTimer(), []);

    const showMore = hasOverflow;

    return (
        <span
            className="rc-rule-title-line"
            ref={lineRef}
            onMouseLeave={close}
        >
            <span className="rc-rule-title-text" ref={summaryRef}>{summary}</span>
            {showMore && (
                <span
                    className={`rc-rule-more-values${disabled ? " no-popover" : ""}`}
                    aria-label="Full rule is available on hover"
                    onMouseEnter={open}
                    ref={anchorRef}
                >
                    ...
                </span>
            )}
            {!disabled && popover && createPortal(
                <span
                    className={`rc-rule-more-popover open ${popover.placement}`}
                    role="tooltip"
                    style={popover.style}
                    onMouseEnter={() => clearCloseTimer()}
                    onMouseLeave={close}
                >
                    <span className="rc-rule-more-popover-content">{children}</span>
                </span>,
                document.body
            )}
        </span>
    );
}

export function CategorizationEditor({ report, selection, editing, catalog, session }: CategorizationEditorProps) {
    const { openRuleSetId, setOpenRuleSetId, openRuleIdsBySet, setOpenRuleIdsBySet } = selection;
    const { reserveUniqueId, replaceSelectedReport, revealAccordionItem } = editing;
    const { lookupOptionsForReportColumn, ruleActionOptions, lookupLabelForReportColumn } = catalog;
    const { simInputs, setSimInputs, setSimRan, simRan } = session;
    // Deleting a rule set that a combined table uses removes rows from that table, so it is
    // confirmed first. Every other delete keeps its existing one-click behaviour.
    const [pendingRuleSetDelete, setPendingRuleSetDelete] = useState<RuleSet | null>(null);

    function deleteRuleSet(ruleSetId: string) {
        replaceSelectedReport((current) => ({ ...current, bucketRuleSets: current.bucketRuleSets.filter((set) => set.id !== ruleSetId), sections: current.sections.map((section) => {
            const withoutList = Array.isArray(section.detailRuleSetIds) && section.detailRuleSetIds.includes(ruleSetId)
                ? { ...section, detailRuleSetIds: section.detailRuleSetIds.filter((id) => id !== ruleSetId) }
                : section;
            // A combined table (active or kept as a draft) drops the rule set and its rows.
            const withoutCombined = combinedRuleSetIds(withoutList).includes(ruleSetId) ? removeCombinedRuleSet(withoutList, ruleSetId) : withoutList;
            if (withoutCombined.ruleSetId !== ruleSetId) return withoutCombined;
            // The single rule set: a combined metric only keeps it as a draft, so its mode stays.
            return usesCombinedRuleSets(withoutCombined)
                ? { ...withoutCombined, ruleSetId: "" }
                : { ...withoutCombined, ruleSetId: "", breakdownSource: "column" as const };
        }) }));
    }

    function renderCategorizationTab(report: ReportConfig) {
        if (!report.models.length) {
            return <section className="rc-block"><h3>Custom Bucket Categorization</h3><p className="rc-hint">Add a Data Model above first, then define reusable Categorization Rule Sets here.</p></section>;
        }
        return (
            <>
                <section className="rc-block">
                    <h3>Custom Bucket Categorization <span className="rc-badge">Reusable</span></h3>
                    <p className="rc-block-hint">Define Categorization Rule Sets once here, then any metric can reuse them. Each Rule Set sorts items into custom categories using conditions on any column, combined with AND/OR.</p>
                    {report.bucketRuleSets.length === 0 && <p className="rc-hint">No Rule Sets yet. Add one below.</p>}
                    <div className="rc-accordion-stack">
                        {report.bucketRuleSets.map((ruleSet, index) => {
                            const used = report.sections.filter((section) => sectionUsesRuleSet(section, ruleSet.id)).length;
                            const ready = ruleSet.rules.length > 0 && ruleSet.rules.every((rule) => isCategorizationRuleReady(report, rule));
                            const elseSummary = ruleSet.includeElse === false
                                ? "Not included"
                                : ruleSet.elseLabel
                                    ? ruleSet.elseLabel
                                    : "Default";
                            return (
                                <ReportAccordionItem
                                    key={ruleSet.id}
                                    id={`ruleset-${ruleSet.id}`}
                                    eyebrow={`Rule Set ${index + 1}`}
                                    title={ruleSet.name || "Untitled Rule Set"}
                                    summary={`${ruleSet.rules.length} Rule${ruleSet.rules.length === 1 ? "" : "s"} - Else: ${elseSummary}`}
                                    meta={<><StatusIcon ready={ready} /><span className="rc-badge">{used} Metric{used === 1 ? "" : "s"}</span></>}
                                    open={openRuleSetId === ruleSet.id}
                                    onOpenChange={(nextOpen) => {
                                        setOpenRuleSetId(nextOpen ? ruleSet.id : "");
                                    }}
                                >
                                    {renderRuleSet(report, ruleSet)}
                                </ReportAccordionItem>
                            );
                        })}
                    </div>
                    <AddActionButton onClick={() => {
                        const usedIds = new Set(report.bucketRuleSets.map((set) => set.id));
                        const nextRuleSet = makeRuleSet(nextCategorizationName(report.bucketRuleSets));
                        nextRuleSet.id = reserveUniqueId("ruleset", usedIds);
                        setOpenRuleSetId(nextRuleSet.id);
                        replaceSelectedReport((current) => ({ ...current, bucketRuleSets: [...current.bucketRuleSets, nextRuleSet] }));
                        revealAccordionItem(`ruleset-${nextRuleSet.id}`);
                    }}>Add Rule Set</AddActionButton>
                </section>
                {/* Rule Simulator is temporarily hidden; keep renderSimulatorBlock for later reuse. */}
                {/* {renderSimulatorBlock(report)} */}
            </>
        );
    }

    function renderRuleSet(report: ReportConfig, ruleSet: RuleSet) {
        const used = report.sections.filter((section) => sectionUsesRuleSet(section, ruleSet.id)).length;
        return (
            <div className="rc-ruleset" data-rc-validation={`ruleset-${ruleSet.id}`}>
                <div className="rc-rule-header">
                    <label>
                        <span>Rule Set Name</span>
                        <input value={ruleSet.name} onChange={(event) => replaceSelectedReport((current) => ({ ...current, bucketRuleSets: current.bucketRuleSets.map((set) => set.id === ruleSet.id ? { ...set, name: event.target.value } : set) }))} />
                    </label>
                    <span className="rc-badge">{used} Metric{used === 1 ? "" : "s"} Using This</span>
                    <span className="rc-spacer" />
                    <IconAction label="Delete Rule Set" onClick={() => {
                        if (report.sections.some((section) => usesCombinedRuleSets(section) && combinedRuleSetIds(section).includes(ruleSet.id))) setPendingRuleSetDelete(ruleSet);
                        else deleteRuleSet(ruleSet.id);
                    }} />
                </div>
                <p className="rc-hint">Rules are checked top to bottom; a row takes the category of the first rule it matches.</p>
                <div className="rc-rule-accordion-stack">
                    {ruleSet.rules.map((rule, index) => {
                        const ready = isCategorizationRuleReady(report, rule);
                        return (
                            <ReportAccordionItem
                                key={rule.id}
                                id={`cat-rule-${ruleSet.id}-${rule.id}`}
                                eyebrow={`Rule ${index + 1}`}
                                title={rulePlainEnglish(report, rule, openRuleIdsBySet[ruleSet.id] !== rule.id)}
                                summary={ruleAccordionSummary(rule)}
                                meta={<StatusIcon ready={ready} />}
                                open={openRuleIdsBySet[ruleSet.id] === rule.id}
                                onOpenChange={(nextOpen) => {
                                    setOpenRuleIdsBySet((current) => ({ ...current, [ruleSet.id]: nextOpen ? rule.id : "" }));
                                }}
                                className="rc-rule-accordion-item"
                            >
                                {renderCatRule(report, ruleSet, rule, index)}
                            </ReportAccordionItem>
                        );
                    })}
                </div>
                <AddActionButton className="compact" onClick={() => {
                    const usedIds = new Set(ruleSet.rules.map((rule) => rule.id));
                    const nextRule = makeCategorizationRule();
                    nextRule.id = reserveUniqueId("rule", usedIds);
                    setOpenRuleIdsBySet((current) => ({ ...current, [ruleSet.id]: nextRule.id }));
                    replaceRuleSet(ruleSet.id, (set) => ({ ...set, rules: [...set.rules, nextRule] }));
                    revealAccordionItem(`cat-rule-${ruleSet.id}-${nextRule.id}`);
                }}>Add Rule</AddActionButton>
                <div className="rc-else-row">
                    <label className="rc-else-head">
                        <input
                            aria-label="Show items that do not match any rule"
                            title="Show items that do not match any rule"
                            type="checkbox"
                            checked={ruleSet.includeElse !== false}
                            onChange={(event) => replaceRuleSet(ruleSet.id, (set) => ({ ...set, includeElse: event.target.checked }))}
                        />
                        <span className="rc-else-title">
                            <span className="rc-lbl">ELSE</span>
                            <span className="rc-else-subtitle">(No Rule Matched)</span>
                        </span>
                    </label>
                    {ruleSet.includeElse !== false ? (
                        <input value={ruleSet.elseLabel} placeholder="Leave blank to keep the original value, or enter a custom category" onChange={(event) => replaceRuleSet(ruleSet.id, (set) => ({ ...set, elseLabel: event.target.value }))} />
                    ) : (
                        <p className="rc-else-disabled">Items that do not match any rule will not be shown in the report.</p>
                    )}
                </div>
            </div>
        );
    }

    // A rule with nested groups can no longer be summarised by slicing a flat list of
    // conditions, so lean on the shared outline builder, which parenthesises each group
    // and so reads with the same precedence as the generated SQL. The accordion title
    // stays one line; collapsed rows expose the complete rule in a structured hover card.
    function rulePlainEnglish(report: ReportConfig, rule: CategorizationRule, showOverflowTooltip = true) {
        const outline = ruleLogicOutline(report, rule);
        const conditionText = outline || "Choose conditions";
        const target = targetFields(report).find((field) => field.key === rule.targetField) || targetFields(report)[0];
        const ruleSentence = (conditions: string) => rule.action === "set"
            ? `If ${conditions}, set ${target?.label || "field"} to ${ruleResultLabel(report, rule) || "..."}`
            : `If ${conditions}, ${rule.action === "include" ? "include" : "exclude"} row`;
        const actionText = rule.action === "set"
            ? `Set ${target?.label || "field"} to ${ruleResultLabel(report, rule) || "..."}`
            : rule.action === "include"
                ? "Include matching rows"
                : "Exclude matching rows";
        const summary = ruleSentence(conditionText);

        return (
            <RuleMoreTooltip summary={summary} disabled={!showOverflowTooltip || !outline}>
                <span className="rc-rule-more-title">Complete rule</span>
                <span className="rc-rule-more-section">
                    <span className="rc-rule-more-label">When</span>
                    <span className="rc-rule-more-bubble">
                        {ruleTooltipSegments(outline || "")}
                    </span>
                </span>
                <span className="rc-rule-more-section">
                    <span className="rc-rule-more-label">Then</span>
                    <span className="rc-rule-more-bubble primary">{actionText}</span>
                </span>
            </RuleMoreTooltip>
        );
    }

    function ruleTooltipSegments(outline: string) {
        const parts = outline.split(/\s+(AND|OR)\s+/).filter(Boolean);
        let connector = "";
        return parts.map((part, index) => {
            if (part === "AND" || part === "OR") {
                connector = part;
                return null;
            }

            const currentConnector = connector;
            connector = "";
            return (
                <span className="rc-rule-more-condition-row" key={`${part}-${index}`}>
                    {currentConnector && <span className="rc-rule-more-connector">{currentConnector}</span>}
                    <span className="rc-rule-more-condition">{part}</span>
                </span>
            );
        });
    }

    function replaceRuleSet(ruleSetId: string, updater: (ruleSet: RuleSet) => RuleSet) {
        replaceSelectedReport((report) => ({ ...report, bucketRuleSets: report.bucketRuleSets.map((set) => set.id === ruleSetId ? updater(set) : set) }));
    }

    function replaceCatRule(ruleSetId: string, ruleId: string, updater: (rule: CategorizationRule) => CategorizationRule) {
        replaceRuleSet(ruleSetId, (set) => ({ ...set, rules: set.rules.map((rule) => rule.id === ruleId ? updater(rule) : rule) }));
    }

    // --- Nested rule group builder -------------------------------------------------
    // rule.conditions is the rule's root group; each member is either a leaf condition
    // or a nested group with its own AND/OR operator. See the tree note in types.ts.
    // RULE_LOGIC_OPTIONS lives at module scope, not here: the component's JSX return
    // sits above these helpers, so a const declared here would still be in its temporal
    // dead zone by the time the renderers below run.

    function ruleAccordionSummary(rule: CategorizationRule) {
        const leaves = ruleLeafConditions(rule.conditions).length;
        if (!leaves) return "No conditions yet";
        const groups = countRuleGroups(rule.conditions);
        const conditionText = `${leaves} condition${leaves === 1 ? "" : "s"}`;
        const groupText = groups ? `, ${groups} group${groups === 1 ? "" : "s"}` : "";
        return `${conditionText}${groupText} - ${rule.action.toUpperCase()}`;
    }

    function countRuleGroups(nodes: RuleNode[]): number {
        return nodes.reduce((total, node) => isRuleGroupNode(node) ? total + 1 + countRuleGroups(node.conditions) : total, 0);
    }

    /** The ctx for one Categorization rule's condition tree. */
    function catRuleTreeCtx(report: ReportConfig, ruleSetId: string, rule: CategorizationRule): RuleTreeCtx {
        return {
            scope: report,
            // Every visible column, not just role === "category". The category pool is what a
            // rule set BUCKETS BY; a rule's conditions may test anything, and restricting them
            // to it meant no date or numeric column could be picked at all - see
            // reportAllColumns.
            columns: reportAllColumns(report),
            // Categorization's own pool is category columns only, so the timestamps a
            // "minutes between" condition needs are supplied separately rather than being
            // mixed into the column dropdown, where they would be offered to every condition.
            timeColumns: reportTimeComparableColumns(report),
            nodes: rule.conditions || [],
            logic: rule.conditionLogic,
            updateNodes: (updater) => replaceCatRule(ruleSetId, rule.id, (current) => ({ ...current, conditions: updater(current.conditions || []) })),
            setRootLogic: (logic) => replaceCatRule(ruleSetId, rule.id, (current) => ({ ...current, conditionLogic: logic })),
            restrictOperatorsByType: false,
        };
    }

    function renderCatRule(report: ReportConfig, ruleSet: RuleSet, rule: CategorizationRule, index: number) {
        const ctx = catRuleTreeCtx(report, ruleSet.id, rule);
        const rootNodes = rule.conditions || [];
        const ready = isCategorizationRuleReady(report, rule);
        const issues = categorizationRuleIssues(report, rule);
        const outline = ruleLogicOutline(report, rule);
        return (
            <div className="rc-groupbox rc-rule-card" key={rule.id} data-rc-validation={`cat-rule-${ruleSet.id}-${rule.id}`}>
                <div className="rc-rule-row rc-rule-card-head">
                    <span className="rc-badge">Rule {index + 1}</span>
                    <span className="rc-hint">IF</span>
                    {rootNodes.length > 1 && (
                        <ValuePicker
                            mode="select"
                            options={RULE_LOGIC_OPTIONS}
                            placeholder={ruleLevelLogicPlaceholder(rootNodes, rule.conditionLogic)}
                            searchPlaceholder="Search Logic..."
                            onPick={(value) => setRuleLevelUniformJoin(ctx, "", value as "and" | "or")}
                        />
                    )}
                    <span className="rc-spacer" />
                    <StatusIcon ready={ready} />
                    <IconAction label="Delete Rule" onClick={() => replaceRuleSet(ruleSet.id, (set) => ({ ...set, rules: set.rules.filter((item) => item.id !== rule.id) }))} />
                </div>
                {outline && <p className="rc-rule-outline"><span className="rc-lbl">Logic</span> <code>{outline}</code></p>}
                {<RuleConditionEditor ctx={ctx} groupId={""} nodes={rootNodes} logic={rule.conditionLogic} depth={0} reserveUniqueId={reserveUniqueId} lookupOptionsForReportColumn={lookupOptionsForReportColumn} lookupLabelForReportColumn={lookupLabelForReportColumn} />}
                {issues.length > 0 && (
                    <ul className="rc-rule-issues">
                        {issues.map((issue) => <li key={`${issue.nodeId}-${issue.kind}`}>{issue.message}</li>)}
                    </ul>
                )}
                {renderThenBlock(report, ruleSet.id, rule)}
            </div>
        );
    }

    function renderThenBlock(report: ReportConfig, ruleSetId: string, rule: CategorizationRule) {
        const availableTargetFields = targetFields(report);
        const selectedTarget = availableTargetFields.find((field) => field.key === rule.targetField) || availableTargetFields[0];
        const targetOptions = selectedTarget.virtual ? [] : lookupOptionsForReportColumn(report, selectedTarget.key);
        // "Set value" source: a typed value (list or custom), or another column's value.
        const fromColumn = rule.valueSource === "column";
        const valueSourceOptions: { id: RuleValueSource; value: RuleValueSource; name: string }[] = [
            { id: "value", value: "value", name: "A Fixed Value" },
            { id: "column", value: "column", name: "Value From Another Column" },
        ];
        const selectedValueSource = valueSourceOptions.find((option) => option.value === (fromColumn ? "column" : "value")) || valueSourceOptions[0];
        // Eligible columns only (not sensitive, showable, has a text form), on the rule set's model.
        const sourceColumns = rule.action === "set" ? ruleValueSourceColumns(report, rule) : [];
        const sourceColumnOptions = sourceColumns.map((column) => ({ id: dataColumnSelectionKey(column) || column.key, value: dataColumnSelectionKey(column) || column.key, name: column.label }));
        const selectedSourceColumn = rule.valueColumn ? sourceColumns.find((column) => columnMatchesKey(column, rule.valueColumn || "")) : undefined;
        const pickValueSource = (value: string) => replaceCatRule(ruleSetId, rule.id, (current) => value === "column"
            ? { ...current, valueSource: "column", valueFromList: "", valueCustom: "" }
            : { ...current, valueSource: "value", valueColumn: "" });
        const summary = rule.action === "set"
            ? <>Result: set <strong>{selectedTarget.label}</strong> = <strong>{ruleResultLabel(report, rule) || "(not set yet)"}</strong></>
            : rule.action === "include"
                ? <>Matching rows will be <strong>included</strong> in the report.</>
                : <>Matching rows will be <strong>excluded</strong> from the report.</>;
        const actionOptions = ruleActionOptions.map((item) => ({ id: item.key, value: item.key, name: item.label }));
        const selectedAction = ruleActionOptions.find((item) => item.key === rule.action) || ruleActionOptions[0];
        const targetPickerOptions = availableTargetFields.map((field) => ({ id: field.key, value: field.key, name: `${field.label}${field.virtual ? " (Output Label)" : ""}` }));
        return (
            <div className="rc-then">
                <div className="rc-then-title"><span className="rc-badge strong">THEN</span><span className="rc-hint">Do This When the Conditions Above Are Met</span></div>
                <div className="rc-then-grid">
                    <label>Action</label><ValuePicker mode="select" options={actionOptions} placeholder={selectedAction.label} onPick={(value) => replaceCatRule(ruleSetId, rule.id, (current) => ({ ...current, action: value as RuleActionKey }))} />
                    {rule.action === "set" && <><label>Target Field</label><ValuePicker mode="select" options={targetPickerOptions} placeholder={`${selectedTarget.label}${selectedTarget.virtual ? " (Output Label)" : ""}`} searchPlaceholder="Search Target Fields..." onPick={(value) => replaceCatRule(ruleSetId, rule.id, (current) => ({ ...current, targetField: value, valueFromList: "", valueCustom: "" }))} /><label>Value Source</label><ValuePicker mode="select" options={valueSourceOptions} placeholder={selectedValueSource.name} onPick={pickValueSource} />
                        <label>Value</label>
                        {fromColumn
                            ? <div className="rc-condition-value-entry rc-then-value-entry"><ValuePicker mode="select" options={sourceColumnOptions} placeholder={selectedSourceColumn?.label || (sourceColumnOptions.length ? "Choose A Column..." : "No Usable Columns On This Model")} searchPlaceholder="Search Columns..." onPick={(value) => replaceCatRule(ruleSetId, rule.id, (current) => ({ ...current, valueSource: "column", valueColumn: value, valueFromList: "", valueCustom: "" }))} /></div>
                            : <div className="rc-condition-value-entry rc-then-value-entry">{targetOptions.length > 0 && <ValuePicker mode="select" options={targetOptions} placeholder={rule.valueFromList ? lookupLabelForReportColumn(report, selectedTarget.key, rule.valueFromList) : "Select From List..."} onPick={(value) => replaceCatRule(ruleSetId, rule.id, (current) => ({ ...current, valueFromList: value, valueCustom: value ? "" : current.valueCustom }))} />}<input className="rc-custom-value" placeholder="Type a Custom Value..." value={rule.valueCustom} onChange={(event) => replaceCatRule(ruleSetId, rule.id, (current) => ({ ...current, valueCustom: event.target.value, valueFromList: event.target.value ? "" : current.valueFromList }))} /></div>}</>}
                </div>
                <p className="rc-hint summary">{summary}</p>
                {rule.action === "set" && fromColumn && (
                    <p className="rc-hint">Each different value of {selectedSourceColumn ? <strong>{selectedSourceColumn.label}</strong> : "the chosen column"} becomes its own category, so charts and summaries cannot list them in advance. Rows where it is blank skip this rule and go on to the next rule, then to the label for everything else.</p>
                )}
            </div>
        );
    }

    /** Hints the shape a simulated value should take for a column with no lookup list. */
    function simulatorPlaceholder(column: DataColumn) {
        const category = columnDataTypeCategory(column);
        if (category === "date") return "YYYY-MM-DD HH:mm";
        if (category === "numeric") return "Number";
        return "Sample Value";
    }

    function renderSimulatorBlock(report: ReportConfig) {
        // Both operands of every condition, not just the left one: a condition comparing two
        // columns, or measuring the time between them, needs a sample value for BOTH or the
        // simulator reads the second as blank and reports a result the SQL would not give.
        const usedColumns = new Set<string>();
        // A column-sourced Set rule writes its source column's value, so that column needs a
        // sample too. Stored as a catalog id, hence matched by identity rather than by key.
        const valueSourceKeys: string[] = [];
        report.bucketRuleSets.forEach((set) => set.rules.forEach((rule) => {
            ruleLeafConditions(rule.conditions).forEach((condition) => {
                if (condition.column) usedColumns.add(condition.column);
                if (condition.compareColumn) usedColumns.add(condition.compareColumn);
            });
            if (rule.action === "set" && rule.valueSource === "column" && rule.valueColumn) valueSourceKeys.push(rule.valueColumn);
        }));
        // reportAllColumns for the same reason the builder uses it - a category-only pool
        // contains no date or numeric column, so any condition on one got no input at all.
        const columns = reportAllColumns(report).filter((column) => usedColumns.has(column.key) || valueSourceKeys.some((key) => columnMatchesKey(column, key)));
        if (!report.bucketRuleSets.length) return <section className="rc-block"><h3>Rule Simulator <span className="rc-badge">Sample Data Preview</span></h3><p className="rc-hint">Create a Rule Set first, then test sample values here.</p></section>;
        if (!columns.length) return <section className="rc-block"><h3>Rule Simulator <span className="rc-badge">Sample Data Preview</span></h3><p className="rc-hint">Add at least one condition with a column chosen to simulate sample data.</p></section>;
        return (
            <section className="rc-block">
                <h3>Rule Simulator <span className="rc-badge">Sample Data Preview</span></h3>
                <p className="rc-block-hint">Enter sample values below and run the simulator to see how your rules would categorize, include, or exclude a row before you save.</p>
                <div className="rc-inline-fields">
                    {columns.map((column) => {
                        // A date or numeric column has no lookup values, so a dropdown would
                        // offer nothing but "-" and the column could never be simulated. Those
                        // fall back to a typed value.
                        const options = lookupOptionsForReportColumn(report, column.key);
                        const setValue = (value: string) => setSimInputs((current) => ({ ...current, [column.key]: value }));
                        return (
                            <label key={column.key}>
                                <span>{column.label}</span>
                                {options.length
                                    ? (
                                        <select value={simInputs[column.key] || ""} onChange={(event) => setValue(event.target.value)}>
                                            <option value="">-</option>
                                            {options.map((option) => <option key={option.id} value={option.value}>{option.name}</option>)}
                                        </select>
                                    )
                                    : (
                                        <input
                                            value={simInputs[column.key] || ""}
                                            onChange={(event) => setValue(event.target.value)}
                                            placeholder={simulatorPlaceholder(column)}
                                        />
                                    )}
                            </label>
                        );
                    })}
                </div>
                <div className="rc-mini-actions"><button className="primary" onClick={() => setSimRan(true)}>Run Simulation</button><button onClick={() => { setSimInputs({}); setSimRan(false); }}>Clear</button></div>
                {simRan && <div className="rc-sim-result"><p className="rc-lbl">Simulation Result for This Sample Row</p>{report.bucketRuleSets.map((set) => {
                    const result = simulateRuleSet(report, set, simInputs);
                    return <div key={set.id}><span className="rc-badge">{set.name}</span><ul>{result.outputs.map((output) => <li key={output.label}>{output.label} <InlineIcon icon="east" label="maps to" /> <strong>{output.value}</strong></li>)}<li>Row would be <strong>{result.included ? "INCLUDED" : "EXCLUDED"}</strong> in the report</li></ul></div>;
                })}</div>}
            </section>
        );
    }
    const nextCategorizationName = (ruleSets: RuleSet[]) => {
        const names = new Set(ruleSets.map((set) => (set.name || "").trim().toLowerCase()));
        let index = ruleSets.length + 1;
        while (names.has(`categorization ${index}`)) index += 1;
        return `Categorization ${index}`;
    };
    return (
        <>
            {renderCategorizationTab(report)}
            <DestructiveConfirmDialog
                open={!!pendingRuleSetDelete}
                kind="report"
                title="Delete Rule Set"
                kicker="Used by a combined table"
                description="This rule set feeds rows of a combined table. Deleting it also removes those rows."
                subject={pendingRuleSetDelete?.name || "Untitled Rule Set"}
                subjectIcon="category"
                impact={pendingRuleSetDelete ? combinedRowsUsingRuleSet(report, pendingRuleSetDelete.id) : []}
                confirmLabel="Delete rule set and rows"
                onCancel={() => setPendingRuleSetDelete(null)}
                onConfirm={() => {
                    if (pendingRuleSetDelete) deleteRuleSet(pendingRuleSetDelete.id);
                    setPendingRuleSetDelete(null);
                }}
            />
        </>
    );
}
